import React, {PropsWithChildren, ReactNode} from 'react'
import {HiXMark} from 'react-icons/hi2'
import {useIsClient} from 'usehooks-ts'
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
} from '~/components/common/Drawer'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from './Dialog'
import {useIsMinMedium} from '~/hooks/useScreen'
import {useTranslations} from 'use-intl'

type ResponsiveDialogProps = PropsWithChildren<{
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    description: ReactNode
    ssr?: boolean
}>

const ResponsiveDialog = ({
    open,
    onOpenChange,
    title,
    description,
    children,
    ssr = false,
}: ResponsiveDialogProps) => {
    const t = useTranslations('dialog')
    const isDesktop = useIsMinMedium()
    const isClient = useIsClient()

    if (!isClient && ssr) {
        return (
            <div role="dialog" aria-modal={true} aria-label={title} className="sr-only">
                <div role="document">
                    <h2>{title}</h2>
                    <p>{description}</p>
                    <div>{children}</div>
                </div>
            </div>
        )
    }

    if (isDesktop) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription className="sr-only">{description}</DialogDescription>
                    </DialogHeader>
                    {children}
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Drawer open={open} onOpenChange={onOpenChange}>
            <DrawerContent>
                <DrawerHeader>
                    <div className="flex flex-row justify-between items-center">
                        <DrawerTitle>{title}</DrawerTitle>
                        <DrawerClose>
                            <HiXMark className="size-7" />
                            <span className="sr-only">{t('close')}</span>
                        </DrawerClose>
                    </div>
                    <DrawerDescription className="sr-only">{description}</DrawerDescription>
                </DrawerHeader>
                {children}
            </DrawerContent>
        </Drawer>
    )
}

export default ResponsiveDialog
